import { useState } from 'react';
import { showSuccess, showError } from '@/utils/toastHelpers';

interface EntityActionsConfig<TEntity, TForm> {
  emptyForm: TForm | (() => TForm);
  deleteMutation: { mutateAsync: (id: any) => Promise<any> };
  entityName: string; // for toast messages, e.g. 'организаторот'
}

export function useEntityActions<TEntity extends { id: any }, TForm>(
  config: EntityActionsConfig<TEntity, TForm>
) {
  const { deleteMutation, entityName } = config;
  const getEmptyForm = () =>
    typeof config.emptyForm === 'function'
      ? (config.emptyForm as () => TForm)()
      : config.emptyForm;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState<TEntity | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [formData, setFormData] = useState<TForm>(getEmptyForm());

  const handleDelete = (id: any) => setDeleteTarget(id);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget);
      showSuccess('Успешно избришано', `${entityName} е успешно избришан.`);
    } catch (error) {
      showError(error, entityName);
    } finally {
      setDeleteTarget(null);
    }
  };

  const openEdit = (entity: TEntity, entityToForm: (e: TEntity) => TForm) => {
    setEditingEntity(entity);
    setFormData(entityToForm(entity));
    setIsDialogOpen(true);
  };

  const openCreate = () => {
    setEditingEntity(null);
    setFormData(getEmptyForm());
    setIsDialogOpen(true);
  };

  const resetDialog = () => {
    setIsDialogOpen(false);
    setEditingEntity(null);
    setFormData(getEmptyForm());
  };

  return {
    isDialogOpen, setIsDialogOpen,
    editingEntity, setEditingEntity,
    deleteTarget, setDeleteTarget,
    formData, setFormData,
    handleDelete, confirmDelete,
    openEdit, openCreate, resetDialog,
  };
}
